import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Avatar, 
  Typography, 
  Button, 
  Form, 
  Input, 
  Upload, 
  message, 
  Divider, 
  Space, 
  Tag, 
  Breadcrumb,
  Spin,
  Alert,
  Modal,
  Popconfirm
} from 'antd';
import { 
  UserOutlined, 
  EditOutlined, 
  SaveOutlined, 
  CameraOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  HomeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  KeyOutlined,
  DeleteOutlined,
  SendOutlined,
  GoogleOutlined,
  SafetyOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useAuth } from '../../AuthProvider';
import { 
  auth, 
  storage, 
  ref, 
  uploadBytes, 
  getDownloadURL,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateEmail,
  updatePassword,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithPopup,
  unlink,
  updateProfile
} from '../../firebase';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const ProfileScreen = () => {
  const { currentUser, tenantType } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  
  // Modal states
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  
  // Loading states
  const [sendingVerification, setSendingVerification] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [unlinkingProvider, setUnlinkingProvider] = useState(false);

  useEffect(() => {
    if (currentUser) {
      // Pre-fill form with existing Firebase user data
      form.setFieldsValue({
        displayName: currentUser.displayName || '',
        email: currentUser.email || '',
        phoneNumber: currentUser.phoneNumber || '',
      });
      
      if (currentUser.photoURL) {
        setAvatarPreview(currentUser.photoURL);
      }
    }
  }, [currentUser, form]);

  const handleAvatarChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file) {
      // Validate file type and size
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;
      
      if (!isImage) {
        message.error('You can only upload image files!');
        return;
      }
      
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !currentUser) return null;
    
    try {
      const storageRef = ref(storage, `user-avatars/${currentUser.uid}_${Date.now()}`);
      const snapshot = await uploadBytes(storageRef, avatarFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      message.error('Failed to upload profile photo');
      return null;
    }
  };

  const handleSave = async (values) => {
    if (!currentUser) return;
    
    try {
      setUpdating(true);
      
      let photoURL = currentUser.photoURL;
      if (avatarFile) {
        photoURL = await uploadAvatar();
      }

      // Update Firebase user profile
      const updateData = {
        displayName: values.displayName,
        ...(photoURL && { photoURL }),
      };

      await updateProfile(currentUser, updateData);
      
      setEditMode(false);
      setAvatarFile(null);
      message.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setAvatarFile(null);
    setAvatarPreview(currentUser?.photoURL || null);
    form.resetFields();
    
    // Restore original values
    form.setFieldsValue({
      displayName: currentUser?.displayName || '',
      email: currentUser?.email || '',
      phoneNumber: currentUser?.phoneNumber || '',
    });
  };

  // Send email verification
  const handleSendEmailVerification = async () => {
    if (!currentUser) return;
    
    try {
      setSendingVerification(true);
      await sendEmailVerification(currentUser);
      message.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification email:', error);
      message.error('Failed to send verification email');
    } finally {
      setSendingVerification(false);
    }
  };

  // Send password reset email
  const handleSendPasswordReset = async () => {
    if (!currentUser?.email) return;
    
    try {
      setSendingPasswordReset(true);
      await sendPasswordResetEmail(auth, currentUser.email);
      message.success('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      message.error('Failed to send password reset email');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  // Update password
  const handleUpdatePassword = async (values) => {
    if (!currentUser) return;
    
    try {
      setUpdating(true);
      
      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(currentUser.email, values.currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, values.newPassword);
      
      setPasswordModalVisible(false);
      passwordForm.resetFields();
      message.success('Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        message.error('Current password is incorrect');
      } else {
        message.error('Failed to update password');
      }
    } finally {
      setUpdating(false);
    }
  };

  // Update email
  const handleUpdateEmail = async (values) => {
    if (!currentUser) return;
    
    try {
      setUpdating(true);
      
      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(currentUser.email, values.password);
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update email
      await updateEmail(currentUser, values.newEmail);
      
      setEmailModalVisible(false);
      emailForm.resetFields();
      message.success('Email updated successfully! Please verify your new email address.');
    } catch (error) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/email-already-in-use') {
        message.error('This email is already in use by another account');
      } else if (error.code === 'auth/wrong-password') {
        message.error('Password is incorrect');
      } else {
        message.error('Failed to update email');
      }
    } finally {
      setUpdating(false);
    }
  };

  // Link Google account
  const handleLinkGoogle = async () => {
    if (!currentUser) return;
    
    try {
      setLinkingGoogle(true);
      const provider = new GoogleAuthProvider();
      await linkWithPopup(currentUser, provider);
      message.success('Google account linked successfully!');
    } catch (error) {
      console.error('Error linking Google account:', error);
      if (error.code === 'auth/credential-already-in-use') {
        message.error('This Google account is already linked to another user');
      } else {
        message.error('Failed to link Google account');
      }
    } finally {
      setLinkingGoogle(false);
    }
  };

  // Unlink provider
  const handleUnlinkProvider = async (providerId) => {
    if (!currentUser) return;
    
    try {
      setUnlinkingProvider(true);
      await unlink(currentUser, providerId);
      message.success('Provider unlinked successfully!');
    } catch (error) {
      console.error('Error unlinking provider:', error);
      message.error('Failed to unlink provider');
    } finally {
      setUnlinkingProvider(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    
    try {
      setUpdating(true);
      await deleteUser(currentUser);
      message.success('Account deleted successfully');
      navigate('/signin');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        message.error('Please sign in again before deleting your account');
      } else {
        message.error('Failed to delete account');
      }
    } finally {
      setUpdating(false);
      setDeleteModalVisible(false);
    }
  };

  const getAccountStatus = () => {
    if (currentUser?.displayName && currentUser?.emailVerified) {
      return { status: 'complete', text: 'Complete', color: 'success' };
    }
    return { status: 'incomplete', text: 'Incomplete', color: 'warning' };
  };

  const getTenantTypeLabel = () => {
    switch (tenantType) {
      case 'employer': return 'Healthcare Facility';
      case 'agency': return 'Staffing Agency';
      case 'employee': return 'Healthcare Professional';
      default: return 'User';
    }
  };

  const getLinkedProviders = () => {
    if (!currentUser?.providerData) return [];
    return currentUser.providerData.map(provider => provider.providerId);
  };

  const hasEmailProvider = () => {
    return getLinkedProviders().includes('password');
  };

  const hasGoogleProvider = () => {
    return getLinkedProviders().includes('google.com');
  };

  if (!currentUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading profile..." />
      </div>
    );
  }

  const accountStatus = getAccountStatus();

  return (
    <div>
      {/* Header Section */}
      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          border: 'none',
          marginBottom: '24px'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Breadcrumb style={{ marginBottom: '16px' }}>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item>
            <UserOutlined />
            Profile
          </Breadcrumb.Item>
        </Breadcrumb>
        
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>
              <UserOutlined style={{ marginRight: '12px' }} />
              My Profile
            </Title>
            <Text style={{ color: '#6b7280', fontSize: '16px' }}>
              Manage your personal account information and settings
            </Text>
          </Col>
          <Col>
            <Space>
              {!editMode ? (
                <Button 
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setEditMode(true)}
                  style={{ 
                    borderRadius: '6px',
                    backgroundColor: '#374151',
                    borderColor: '#374151'
                  }}
                >
                  Edit Profile
                </Button>
              ) : (
                <Space>
                  <Button 
                    onClick={handleCancel}
                    style={{ 
                      borderRadius: '6px',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={updating}
                    onClick={() => form.submit()}
                    style={{ 
                      borderRadius: '6px',
                      backgroundColor: '#374151',
                      borderColor: '#374151'
                    }}
                  >
                    Save Changes
                  </Button>
                </Space>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Profile Overview Card */}
        <Col xs={24} lg={8}>
          <Card 
            style={{ 
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              height: 'fit-content'
            }}
            bodyStyle={{ padding: '24px', textAlign: 'center' }}
          >
            <div style={{ marginBottom: '20px' }}>
              {editMode ? (
                <Upload
                  name="avatar"
                  listType="picture-card"
                  className="avatar-uploader"
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleAvatarChange}
                  style={{ marginBottom: '16px' }}
                >
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '8px', objectFit: 'cover' }} />
                  ) : (
                    <div>
                      <CameraOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                      <div>Upload Photo</div>
                    </div>
                  )}
                </Upload>
              ) : (
                <Avatar 
                  size={120} 
                  src={avatarPreview}
                  icon={<UserOutlined />}
                  style={{ 
                    backgroundColor: '#374151',
                    marginBottom: '16px'
                  }}
                />
              )}
            </div>
            
            <Title level={3} style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
              {currentUser.displayName || 'Set Display Name'}
            </Title>
            
            <Text type="secondary" style={{ fontSize: '14px', marginBottom: '16px', display: 'block' }}>
              {currentUser.email}
            </Text>
            
            <Tag 
              color="blue" 
              style={{ 
                marginBottom: '16px',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px'
              }}
            >
              {getTenantTypeLabel()}
            </Tag>
            
            <div style={{ marginBottom: '20px' }}>
              <Tag 
                icon={accountStatus.status === 'complete' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                color={accountStatus.color}
                style={{ 
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                Profile {accountStatus.text}
              </Tag>
            </div>

            {/* Email Verification Status */}
            {!currentUser.emailVerified && (
              <div style={{ marginBottom: '16px' }}>
                <Alert
                  message="Email not verified"
                  type="warning"
                  showIcon
                  style={{ marginBottom: '8px', borderRadius: '6px' }}
                />
                <Button 
                  type="link" 
                  size="small"
                  icon={<SendOutlined />}
                  loading={sendingVerification}
                  onClick={handleSendEmailVerification}
                  style={{ padding: 0 }}
                >
                  Send verification email
                </Button>
              </div>
            )}

            <Divider />

            <div style={{ textAlign: 'left' }}>
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#374151' }}>Account Created</Text>
                <br />
                <Text type="secondary">
                  {currentUser.metadata?.creationTime ? 
                    new Date(currentUser.metadata.creationTime).toLocaleDateString() : 
                    'N/A'
                  }
                </Text>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#374151' }}>Last Sign In</Text>
                <br />
                <Text type="secondary">
                  {currentUser.metadata?.lastSignInTime ? 
                    new Date(currentUser.metadata.lastSignInTime).toLocaleDateString() : 
                    'N/A'
                  }
                </Text>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#374151' }}>Email Verified</Text>
                <br />
                <Tag 
                  color={currentUser.emailVerified ? 'success' : 'warning'}
                  style={{ borderRadius: '4px' }}
                >
                  {currentUser.emailVerified ? 'Verified' : 'Not Verified'}
                </Tag>
              </div>

              <div>
                <Text strong style={{ color: '#374151' }}>User ID</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                  {currentUser.uid}
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* Profile Details Form */}
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Personal Information Card */}
            <Card 
              title={
                <Space>
                  <UserOutlined />
                  Personal Information
                </Space>
              }
              style={{ 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              {accountStatus.status === 'incomplete' && (
                <Alert
                  message="Complete Your Profile"
                  description="Please add a display name and verify your email to complete your profile setup."
                  type="warning"
                  showIcon
                  style={{ marginBottom: '24px', borderRadius: '6px' }}
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                disabled={!editMode}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="displayName" 
                      label="Display Name"
                      rules={[{ required: true, message: 'Please enter your display name' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="Enter your display name"
                        prefix={<UserOutlined style={{ color: '#6b7280' }} />}
                        style={{ borderRadius: '6px' }}
                      />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="email" 
                      label="Email Address"
                    >
                      <Input 
                        size="large"
                        placeholder="Email address"
                        prefix={<MailOutlined style={{ color: '#6b7280' }} />}
                        style={{ borderRadius: '6px' }}
                        disabled // Firebase user email is managed separately
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="phoneNumber" 
                      label="Phone Number"
                    >
                      <Input 
                        size="large"
                        placeholder="Phone number (managed by Firebase Auth)"
                        prefix={<PhoneOutlined style={{ color: '#6b7280' }} />}
                        style={{ borderRadius: '6px' }}
                        disabled // Firebase phone number is managed separately
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

            {/* Account Security Card */}
            <Card 
              title={
                <Space>
                  <LockOutlined />
                  Account Security
                </Space>
              }
              style={{ 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Row gutter={[16, 24]}>
                {/* Password Management */}
                {hasEmailProvider() && (
                  <>
                    <Col span={24}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>Password</Text>
                          <br />
                          <Text type="secondary">Change your account password</Text>
                        </div>
                        <Space>
                          <Button 
                            icon={<KeyOutlined />}
                            onClick={() => setPasswordModalVisible(true)}
                            style={{ borderRadius: '6px' }}
                          >
                            Change Password
                          </Button>
                          <Button 
                            icon={<SendOutlined />}
                            loading={sendingPasswordReset}
                            onClick={handleSendPasswordReset}
                            style={{ borderRadius: '6px' }}
                          >
                            Reset Password
                          </Button>
                        </Space>
                      </div>
                    </Col>
                    <Col span={24}><Divider /></Col>
                  </>
                )}

                {/* Email Management */}
                <Col span={24}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text strong>Email Address</Text>
                      <br />
                      <Text type="secondary">
                        {currentUser.email}
                        {currentUser.emailVerified ? 
                          <Tag color="success" style={{ marginLeft: '8px' }}>Verified</Tag> : 
                          <Tag color="warning" style={{ marginLeft: '8px' }}>Not Verified</Tag>
                        }
                      </Text>
                    </div>
                    <Space>
                      {hasEmailProvider() && (
                        <Button 
                          icon={<MailOutlined />}
                          onClick={() => setEmailModalVisible(true)}
                          style={{ borderRadius: '6px' }}
                        >
                          Change Email
                        </Button>
                      )}
                      {!currentUser.emailVerified && (
                        <Button 
                          icon={<SendOutlined />}
                          loading={sendingVerification}
                          onClick={handleSendEmailVerification}
                          type="primary"
                          style={{ borderRadius: '6px' }}
                        >
                          Verify Email
                        </Button>
                      )}
                    </Space>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Linked Accounts Card */}
            <Card 
              title={
                <Space>
                  <SafetyOutlined />
                  Linked Accounts
                </Space>
              }
              style={{ 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Row gutter={[16, 16]}>
                {/* Email/Password Provider */}
                <Col span={24}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <MailOutlined style={{ fontSize: '20px', marginRight: '12px', color: '#1890ff' }} />
                      <div>
                        <Text strong>Email & Password</Text>
                        <br />
                        <Text type="secondary">Sign in with email and password</Text>
                      </div>
                    </div>
                    <Tag color={hasEmailProvider() ? 'success' : 'default'}>
                      {hasEmailProvider() ? 'Connected' : 'Not Connected'}
                    </Tag>
                  </div>
                </Col>

                <Col span={24}><Divider /></Col>

                {/* Google Provider */}
                <Col span={24}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <GoogleOutlined style={{ fontSize: '20px', marginRight: '12px', color: '#db4437' }} />
                      <div>
                        <Text strong>Google Account</Text>
                        <br />
                        <Text type="secondary">Sign in with your Google account</Text>
                      </div>
                    </div>
                    <Space>
                      {hasGoogleProvider() ? (
                        <>
                          <Tag color="success">Connected</Tag>
                          {getLinkedProviders().length > 1 && (
                            <Popconfirm
                              title="Unlink Google Account"
                              description="Are you sure you want to unlink your Google account?"
                              onConfirm={() => handleUnlinkProvider('google.com')}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button 
                                size="small"
                                loading={unlinkingProvider}
                                style={{ borderRadius: '4px' }}
                              >
                                Unlink
                              </Button>
                            </Popconfirm>
                          )}
                        </>
                      ) : (
                        <>
                          <Tag color="default">Not Connected</Tag>
                          <Button 
                            icon={<GoogleOutlined />}
                            loading={linkingGoogle}
                            onClick={handleLinkGoogle}
                            style={{ borderRadius: '6px' }}
                          >
                            Link Google
                          </Button>
                        </>
                      )}
                    </Space>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Danger Zone Card */}
            <Card 
              title={
                <Space>
                  <WarningOutlined style={{ color: '#dc2626' }} />
                  <span style={{ color: '#dc2626' }}>Danger Zone</span>
                </Space>
              }
              style={{ 
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                border: '1px solid #fecaca',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong style={{ color: '#dc2626' }}>Delete Account</Text>
                  <br />
                  <Text type="secondary">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </Text>
                </div>
                <Popconfirm
                  title="Delete Account"
                  description="Are you sure you want to permanently delete your account? This action cannot be undone."
                  onConfirm={() => setDeleteModalVisible(true)}
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                    style={{ borderRadius: '6px' }}
                  >
                    Delete Account
                  </Button>
                </Popconfirm>
              </div>
            </Card>
          </Space>
        </Col>
      </Row>

      {/* Change Password Modal */}
      <Modal
        title={
          <Space>
            <KeyOutlined />
            Change Password
          </Space>
        }
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleUpdatePassword}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password 
              size="large"
              placeholder="Enter current password"
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password 
              size="large"
              placeholder="Enter new password"
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password 
              size="large"
              placeholder="Confirm new password"
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => {
                  setPasswordModalVisible(false);
                  passwordForm.resetFields();
                }}
                style={{ borderRadius: '6px' }}
              >
                Cancel
              </Button>
              <Button 
                type="primary"
                htmlType="submit"
                loading={updating}
                icon={<SaveOutlined />}
                style={{ borderRadius: '6px' }}
              >
                Update Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Email Modal */}
      <Modal
        title={
          <Space>
            <MailOutlined />
            Change Email Address
          </Space>
        }
        open={emailModalVisible}
        onCancel={() => {
          setEmailModalVisible(false);
          emailForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Alert
          message="Email Change Notice"
          description="After changing your email, you'll need to verify the new email address before you can use it to sign in."
          type="info"
          showIcon
          style={{ marginBottom: '16px', borderRadius: '6px' }}
        />

        <Form
          form={emailForm}
          layout="vertical"
          onFinish={handleUpdateEmail}
        >
          <Form.Item
            name="newEmail"
            label="New Email Address"
            rules={[
              { required: true, message: 'Please enter your new email address' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input 
              size="large"
              placeholder="Enter new email address"
              prefix={<MailOutlined style={{ color: '#6b7280' }} />}
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password to confirm' }]}
          >
            <Input.Password 
              size="large"
              placeholder="Enter current password"
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => {
                  setEmailModalVisible(false);
                  emailForm.resetFields();
                }}
                style={{ borderRadius: '6px' }}
              >
                Cancel
              </Button>
              <Button 
                type="primary"
                htmlType="submit"
                loading={updating}
                icon={<SaveOutlined />}
                style={{ borderRadius: '6px' }}
              >
                Update Email
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#dc2626' }} />
            <span style={{ color: '#dc2626' }}>Delete Account</span>
          </Space>
        }
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        width={500}
      >
        <Alert
          message="This action cannot be undone"
          description="Deleting your account will permanently remove all your data, including your profile, assignments, and any associated information. This action is irreversible."
          type="error"
          showIcon
          style={{ marginBottom: '16px', borderRadius: '6px' }}
        />

        <div style={{ marginTop: '24px' }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button 
              onClick={() => setDeleteModalVisible(false)}
              style={{ borderRadius: '6px' }}
            >
              Cancel
            </Button>
            <Button 
              danger
              loading={updating}
              onClick={handleDeleteAccount}
              icon={<DeleteOutlined />}
              style={{ borderRadius: '6px' }}
            >
              Delete My Account
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default ProfileScreen;
